import { ChangeEventHandler, FC, FormEvent, useCallback, useState } from 'react';
import { ErrorNotification, Input, Password, PrimaryButton, SecondaryCardButton } from '@cb/apricot-react';
import { Grid, LinearProgress } from '@mui/material';

import { useAuth } from 'src/contexts';
import { WhatIsSchoolCodeModal, WhatIsScoreSendCodeModal } from 'src/components';
import { APP_URLS } from 'src/constants';
import { useNavigate } from 'react-router-dom';

const Login: FC = () => {
  const { onLogIn } = useAuth();
  const navigate = useNavigate();
  const { isAI, isDI } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showFormStatus, setShowFormStatus] = useState(false);

  const [form, setForm] = useState({
    name: '',
    password: '',
  });

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setForm({
      ...form,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = useCallback(
      async (event: FormEvent) => {
        event.preventDefault();

        if (!form.name || !form.password) {
          setShowFormStatus(true);
          return;
        }

        setIsLoading(true);
        try {
          await onLogIn(form.name, form.password);
          navigate(APP_URLS.HOME);
        } catch (error: any) {
          setErrorMessage(error.message);
        } finally {
          setIsLoading(false);
        }
      },
      [form, navigate, onLogIn],
  );

  const handleClickForgotPassword = useCallback(() => {
    navigate(APP_URLS.FORGOT_PASSWORD);
  }, [navigate]);
  const handleClickSignup = useCallback(() => {
    navigate(APP_URLS.REGISTER);
  }, [navigate]);

  return (
      <div className="MuiGrid-root MuiGrid-container css-1oabc34-MuiGrid-root">
        <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-12 css-1idn90j-MuiGrid-root">
          <div className="cb-band cb-band-hero cb-band-hero-card cb-blue5-bg cb-white-color">
            <div className="cb-band-hero-card-polygon cb-tint-blue5-1"></div>
            <div className="container">
              <div className="row">
                <div className="col-xs-10 offset-xs-1 col-md-8 offset-md-0">
                  {isAI && 
                  <div>
                    <div className="cb-band-hero-content cb-white-color"><h1
                      className="cb-band-hero-title">College Board</h1><p className="cb-band-hero-desc"><b>School
                      Code Request Form</b> : <br />College Board School Codes are assigned to organizations
                      that want to receive scores for or administer College Board assessments.<br />Log in or
                      sign up to request a College Board School Code for the first time, to update an
                      existing College Board School Code to Level II, or to update school data (including school name and address) for an existing College Board School Code.</p>
                    </div>
                  </div>}
                  {isDI && 
                  <div>
                    <div className="cb-band-hero-content cb-white-color"><h1
                      className="cb-band-hero-title">College Board</h1><p className="cb-band-hero-desc"><b>Score
                      Send Code Request Form</b> : <br />College Board Score Send Codes are assigned to organizations that wish to receive College Board assessment scores in order to offer post-secondary opportunities to students.<br />Log in or
                      sign up to request a College Board Score Send Code for the first time, to update an existing College Board Score Send Code to accept more programs, or to update institution data (including name and address) for an existing College Board Score Send Code.</p>
                    </div>
                  </div>}
                </div>
                <div className="col-xs-10 offset-xs-1 col-md-4 offset-md-0">
                  <div className="cb-card cb-identity-card cb-align-center cb-black1-color" style={{padding: '0px'}} id="identityCardComponent">

                    <div className="cb-identity-card-container">
                      <Grid container justifyContent="left" sx={{ p: 3 }}>

                        <form onSubmit={handleSubmit}>
                          {isLoading && <LinearProgress color="secondary" />}
                          {errorMessage && (
                              // @ts-ignore
                              <ErrorNotification dismissible title="Error">
                                {errorMessage}
                              </ErrorNotification>
                          )}

                          <Grid container spacing={3} sx={{ p: 3, px: 5 }}>
                            <Grid item xs={12}>
                              {isAI && 
                              <div>
                              <Grid container justifyContent="center">
                                <SecondaryCardButton aria-haspopup="dialog" id="whatIsSchoolCodeModal">
                                  What is a School Code?
                                </SecondaryCardButton>
                              </Grid>

                              <WhatIsSchoolCodeModal trigger="whatIsSchoolCodeModal" />
                              </div>
                              }
                              {isDI && 
                              <div>
                              <Grid container justifyContent="center">
                                <SecondaryCardButton aria-haspopup="dialog" id="whatIsScoreSendCodeModal">
                                  What is a Score Send Code?
                                </SecondaryCardButton>
                              </Grid>

                              <WhatIsScoreSendCodeModal trigger="whatIsScoreSendCodeModal" />
                              </div>
                              }
                            </Grid>

                            <Grid item xs={12}>
                              <Input
                                  name="name"
                                  label="Username"
                                  required
                                  value={form.name}
                                  validation={showFormStatus && !form.name ? 'error' : undefined}
                                  validationMsg="User name is required"
                                  onChange={handleChange}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Password
                                  name="password"
                                  label="Password"
                                  required
                                  value={form.password}
                                  validation={showFormStatus && !form.password ? 'error' : undefined}
                                  validationMsg="User password is required"
                                  onChange={handleChange}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Grid container justifyContent="space-between" alignItems="center">
                                <PrimaryButton type="submit">LogIn</PrimaryButton>

                                <SecondaryCardButton onClick={handleClickForgotPassword}>Forgot Password?</SecondaryCardButton>
                                <SecondaryCardButton onClick={handleClickSignup}>Create Account</SecondaryCardButton>
                              </Grid>
                            </Grid>
                          </Grid>
                        </form>

                      </Grid>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Login;
